import React from 'react';
import { motion } from 'framer-motion';

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/e8b39f20-ada1-4d28-b88c-fa6cc56a8377/images/2b6c88d22f010b93032fec90ea09d78e1db428c32b8d239d96c2baa94b425431.png"
          alt="Indian athletes playing sports"
          className="w-full h-full object-cover"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/60 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
      </div>

      {/* Background dots pattern */}
      <div className="absolute inset-0 bg-dots opacity-30 z-0" />

      {/* Content */}
      <motion.div
        className="container-custom relative z-10 text-center px-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Overline */}
        <motion.p
          variants={itemVariants}
          className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-6"
        >
          India's Sports Networking App
        </motion.p>

        {/* Main Headline */}
        <motion.h1
          variants={itemVariants}
          className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white text-shadow mb-6"
        >
          <span className="gradient-text">Find</span> <span className="text-primary">.</span> <span className="text-white">Connect</span> <span className="text-primary">.</span> <span className="gradient-text">Play</span> <span className="text-primary">.</span> <span className="text-white">Grow</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          variants={itemVariants}
          className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          <span className="text-white/80">with Players, Crew, Clubs, and Venues around you!</span>
        </motion.p>

        {/* App Store Badges & QR Code */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col lg:flex-row items-center justify-center gap-8"
          id="download"
        >
          {/* Download Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <motion.a
              href="https://play.google.com/store/apps/details?id=com.app.playmatez&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="google-play-btn"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                alt="Get it on Google Play" 
                className="h-14"
              />
            </motion.a>

            <motion.a
              href="https://apps.apple.com/app/playmatez/id6450857903"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="app-store-btn"
            >
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                alt="Download on the App Store" 
                className="h-14"
              />
            </motion.a>
          </div>

          {/* QR Code Section */}
          <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
            <div className="bg-white p-3 rounded-xl">
              <img
                src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://download.playmatez.io/u/F4AE8rel&bgcolor=ffffff&color=000000"
                alt="Download Playmatez QR Code"
                className="w-20 h-20"
                data-testid="qr-code"
              />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Scan to Download</p>
              <p className="text-muted-foreground text-xs">Point your camera at the QR code</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-primary rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
